/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Mar 29 2024
 **/

import { useCallback } from "react";
import * as tus from "tus-js-client";
import { atom, useAtomValue } from "jotai";
import { atomFamily, useAtomCallback } from "jotai/utils";
import { cloneDeep } from "lodash-es";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import { Properties } from "@/frameworks/libraries/properties";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { Configure } from "@/frameworks/libraries/utilities";

type TusEvents = {
	onError?: (error: Error | tus.DetailedError) => void;
	onProgress?: (bytesUploaded: number, bytesTotal: number, percentage: number) => void;
	onSuccess?: () => void;
};

type TusOptions = TusEvents;

type Status = "success" | "pending" | "error";
type Target = "FILEBOX" | "CHAT" | "SPACE";

type UploadProgressState = {
	percentage: number;
	fileName: string;
	fileMimeType: string;
	status: Status;
	target: Target;
	uploader: tus.Upload;
};

type UploadProgressStateMap = Record<string, UploadProgressState>;

// const fileAPI = new FileAPI();

const tusUploadStateAtom = atom<UploadProgressStateMap>({});
const tusUploadStateAtomFamily = atomFamily(
	(tusID: string) => {
		return atom((get) => {
			return get(tusUploadStateAtom)[tusID];
		});
	},
	(prev, next) => prev === next
);
const tusUploadTargetAtomFamily = atomFamily(
	(target: Target) => {
		return atom((get) => {
			const tusStates = cloneDeep(get(tusUploadStateAtom));
			Object.keys(tusStates).forEach((v) => {
				if (tusStates[v].target !== target) {
					delete tusStates[v];
				}
			});

			return tusStates;
		});
	},
	(prev, next) => prev === next
);

export function useUploadTus() {
	const token = Configure.authorizationKey;

	const { data: chunkSize = 50 } = useQuery({
		queryKey: ["TUS_CHUNK_SIZE"],
		queryFn: async () => {
			return (
				await axios.get<FileBox.Tus.GetTusChunkSizeResponse>(`${Properties.property.apiDomain}/filebox/tus`, {
					headers: {
						Authorization: Configure.authorizationKey
					}
				})
			).data.chunkSize;
		},
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnMount: false,
		refetchOnWindowFocus: false
	});

	const getTusUploadState = useAtomCallback(
		useCallback((get, _, tusID: string) => {
			return get(tusUploadStateAtom)[tusID];
		}, []),
		{ store: JotaiStore }
	);

	const updateUploadedState = useAtomCallback(
		useCallback((_, set, tusID: string, status: Status) => {
			return set(tusUploadStateAtom, (prev) => {
				const result = { ...prev };
				result[tusID].status = status;
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const updateUploadProgress = useAtomCallback(
		useCallback((_, set, tusID: string, percentage: number) => {
			return set(tusUploadStateAtom, (prev) => {
				const result = { ...prev };
				result[tusID].percentage = percentage;
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const clearUploadedState = useAtomCallback(
		useCallback((_, set) => {
			return set(tusUploadStateAtom, {});
		}, []),
		{ store: JotaiStore }
	);

	const removeUploadedState = useAtomCallback(
		useCallback((_, set, tusID: string) => {
			return set(tusUploadStateAtom, (prev) => {
				const result = { ...prev };
				delete result[tusID];
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const initTusState = useAtomCallback(
		useCallback((_, set, tusID: string, state: UploadProgressState) => {
			return set(tusUploadStateAtom, (prev) => {
				const result = { ...prev };
				result[tusID] = state;
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const cancelUpload = useAtomCallback(
		useCallback((_, set, tusID: string, shouldTerminate?: boolean) => {
			return set(tusUploadStateAtom, (prev) => {
				const result = { ...prev };
				const target = { ...result[tusID] };
				target.status = "error";
				target.uploader.abort(shouldTerminate);
				result[tusID] = target;
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const uploadTus = useCallback(
		(file: File, options?: TusOptions) => {
			const upload = new tus.Upload(file, {
				// Endpoint is the upload creation URL from your tus server
				endpoint: `${Properties.property.apiDomain}/filebox/tus/upload`,
				// Retry delays will enable tus-js-client to automatically retry on errors
				retryDelays: [0, 3000, 5000, 10000, 20000],
				chunkSize: chunkSize * 1024 * 1024,
				// Attach additional meta data about the file for the server
				metadata: {
					filename: file.name,
					filetype: file.type
				},
				headers: {
					Authorization: token ?? ""
				},
				// Callback for errors which cannot be fixed using retries
				onError: options?.onError,
				// Callback for reporting upload progress
				onProgress: function (bytesUploaded, bytesTotal) {
					const percentage = Number(((bytesUploaded / bytesTotal) * 100).toFixed(0));
					options?.onProgress?.(bytesUploaded, bytesTotal, Number(percentage));
				},
				// Callback for once the upload is completed
				onSuccess: options?.onSuccess,
				storeFingerprintForResuming: false
			});

			// Check if there are any previous uploads to continue.
			upload.findPreviousUploads().then(function (previousUploads) {
				// Found previous uploads so we select the first one.
				if (previousUploads.length) {
					upload.resumeFromPreviousUpload(previousUploads[0]);
				}
				// Start the upload
				upload.start();
			});

			return upload;
		},
		[chunkSize]
	);

	return {
		uploadTus,
		getTusUploadState,
		updateUploadedState,
		clearUploadedState,
		removeUploadedState,
		updateUploadProgress,
		initTusState,
		cancelUpload
	};
}

export function useTusUploadStateRenderer(tusID: string) {
	return useAtomValue(tusUploadStateAtomFamily(tusID), { store: JotaiStore });
}

export function useAllTusUploadStateRenderer() {
	return useAtomValue(tusUploadStateAtom, { store: JotaiStore });
}

export function useTusUploadTargetRenderer(target: Target) {
	return useAtomValue(tusUploadTargetAtomFamily(target), { store: JotaiStore });
}
